import React, { useCallback, useRef, useState } from 'react'
import { ActivityIndicator, LayoutChangeEvent, ScrollView, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router'

import { DS } from '@/design-system'
import { fontFamily } from '../../theme'
import SegmentControl from '@/components/ANewDesign/SegmentControl/SegmentControl'
import { Button } from '@/components/ANewDesign/Button/Button'
import { WebWrapper } from '@/components/WebWrapper'
import { ScreenHeader } from '@/components/ScreenHeader'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { getAdvisorCoachingLevel, useActionAdvisorStore } from '@/screens/GameContainer/useActionAdvisorStore'
import { translate } from '../../i18n'
import { useCreateGameStore, type SoloAdvisorLevel } from '../../stores/create-game-store'
import { SoloTab, SoloTabHandle } from './SoloTab'
import { GroupTab, GroupTabHandle } from './GroupTab'

const TABS = [translate('createGameV2.tabs.solo'), translate('createGameV2.tabs.group')]

/** Applies the create-solo-form's advisor level to the advisor store — the
 * same three levels the in-game drawer offers. Beginner/Intermediate also
 * UNMUTE the avatar: the form explicitly opts the player in, so the advisor
 * starts active in the new game rather than grayscale-mute (the drawer's
 * level only owns WHAT the advisor does once unmuted). Returns the pre-apply
 * level and mute state so the caller can log the transition. */
function applySoloAdvisorLevel(level: SoloAdvisorLevel) {
  const advisor = useActionAdvisorStore.getState()
  const fromLevel = getAdvisorCoachingLevel(advisor)
  const wasMuted = !advisor.feedbackEnabled
  if (level === 'beginner') {
    advisor.setEnabled(true)
    advisor.setAdviceBeforePlay(true)
    advisor.setFeedbackEnabled(true)
  } else if (level === 'intermediate') {
    advisor.setEnabled(true)
    advisor.setAdviceBeforePlay(false)
    advisor.setFeedbackEnabled(true)
  } else {
    advisor.setEnabled(false)
    advisor.setFeedbackEnabled(false)
    advisor.clear()
  }
  return { fromLevel, wasMuted }
}

export function CreateGameScreen() {
  const { loungeId: loungeIdParam, simple: simpleParam } = useLocalSearchParams<{ loungeId?: string, simple?: string }>()
  const loungeId = loungeIdParam as Id<'lounges'> | undefined
  const isLounge = Boolean(loungeId)
  const isSimple = !isLounge && simpleParam === 'true'

  const [activeTab, setActiveTab] = useState(0)
  const [isCreating, setIsCreating] = useState(false)
  const [containerWidth, setContainerWidth] = useState(0)
  const [repeatDays, setRepeatDays] = useState<number[]>([])
  const soloTabRef = useRef<SoloTabHandle>(null)
  const groupTabRef = useRef<GroupTabHandle>(null)

  const {
    session: { user },
    analytics: { setEvent }
  } = useStore()
  const userId = user?.auth0Id ?? 'guest'
  const userName = user?.name ?? 'Guest Player'
  const mongoUserId = user?._id?.toString()

  const { solo, group, reset, setSolo } = useCreateGameStore()

  useFocusEffect(
    useCallback(() => {
      reset()
      // Seed the form's advisor level from the player's SAVED level (set
      // in-game via the drawer, or by a previous create — the create flow
      // writes the picked level back on start). A fresh player gets the
      // store default (Advice & Feedback); a returning player gets what
      // they chose last time (e.g. Off or Feedback Only). Only the level
      // seeds — the avatar mute is a separate, session-y axis.
      setSolo({ advisorLevel: getAdvisorCoachingLevel(useActionAdvisorStore.getState()) })
    }, [reset, setSolo])
  )
  const createPracticeGame = useMutation(api.games.mutations.createPracticeGame)
  const createPrivateGame = useMutation(api.games.mutations.createPrivateGame)
  const createPrivateSchedule = useMutation(api.gameSchedules.mutations.createPrivateSchedule)
  const createLoungeScheduledGame = useMutation(api.lounges.mutations.createLoungeScheduledGame)
  const announceGame = useMutation(api.lounges.mutations.announceGame)
  const joinGame = useMutation(api.games.mutations.joinGame)

  const handleContainerLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width)
  }

  const isSchedulingGroupGame = (isLounge || activeTab === 1) && group.scheduleForLater

  const handleStartSoloGame = async () => {
    if (!soloTabRef.current?.validate()) return
    try {
      setIsCreating(true)
      const result = await createPracticeGame({
        name: solo.gameName.trim() || translate('gameMode.practice'),
        bigBlind: solo.bigBlind,
        botCount: solo.botCount,
        botSkill: solo.botSkill,
        startingChips: solo.startingChips,
        timeBetweenBlindsIncrease: solo.blindIncreases ? solo.blindInterval * 60 * 1000 : 0,
        turnDuration: solo.turnDuration * 1000
      })

      if (!result?.gameId) throw new Error('Failed to create game')

      await joinGame({ gameId: result.gameId, userId, mongoUserId, name: userName })

      // Apply the advisor level picked in the form (default: Advice &
      // Feedback) before entering the game, so a fresh solo game starts with
      // the advisor active at that level.
      const { fromLevel, wasMuted } = applySoloAdvisorLevel(solo.advisorLevel)
      if (fromLevel !== solo.advisorLevel) {
        setEvent(events.advisor.level_changed, {
          from: fromLevel,
          to: solo.advisorLevel,
          coachingLevel: solo.advisorLevel,
          gameMode: 'practice'
        })
      }
      if (wasMuted && solo.advisorLevel !== 'off') {
        setEvent(events.advisor.unmuted, { coachingLevel: solo.advisorLevel, gameMode: 'practice' })
      }

      router.push({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as never)
    } catch (error) {
      console.error('Failed to create game:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleStartGroupGame = async () => {
    if (!groupTabRef.current?.validate()) return
    try {
      setIsCreating(true)
      const result = await createPrivateGame({
        name: group.gameName.trim() || `${userName}'s Game`,
        userId,
        bigBlind: group.bigBlind,
        startingChips: group.startingChips,
        timeBetweenBlindsIncrease: group.blindIncreases ? group.blindInterval * 60 * 1000 : 0,
        turnDuration: group.turnDuration * 1000,
        maxRebuys: group.rebuy ? group.rebuyCount : 0,
        ...(isLounge && loungeId ? { loungeId } : {})
      })

      if (!result?.gameId) throw new Error('Failed to create game')

      // Teachers land as observers and pick play / observe in-game (TeacherSeatChoiceModal).
      await joinGame({ gameId: result.gameId, userId, mongoUserId, name: userName })

      if (isLounge && loungeId) {
        // Announce to the lounge, then tear down the whole create flow as we navigate into the game
        // so leaving it returns to the lounge rather than back into this screen.
        await announceGame({ loungeId, userId, userName, gameId: result.gameId })
        router.dismissTo({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as never)
      } else {
        router.push({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as never)
      }
    } catch (error) {
      console.error('Failed to create game:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleScheduleGroupGame = async () => {
    if (!groupTabRef.current?.validate()) return
    try {
      setIsCreating(true)
      if (isLounge && loungeId) {
        await createLoungeScheduledGame({
          loungeId,
          userId,
          userName,
          name: group.gameName.trim() || `${userName}'s Game`,
          scheduledStartTime: group.scheduleDate.getTime(),
          // 0 (not undefined) means "no blind increase" — undefined would fall back to the
          // default blind timer when the schedule spawns its game.
          blindTimer: group.blindIncreases ? group.blindInterval : 0,
          startingChips: group.startingChips,
          turnDuration: group.turnDuration,
          bigBlind: group.bigBlind,
          // Rebuy cap only has somewhere to live on a one-off schedule — recurring schedules have no
          // maxRebuys field, so GroupTab already hides the toggle once a repeat day is picked.
          // "None" must be 0: an omitted maxRebuys means unlimited (PP-4881).
          maxRebuys: repeatDays.length === 0 ? (group.rebuy ? group.rebuyCount : 0) : undefined,
          repeatDaysOfWeekUTC: repeatDays
        })

        if (router.canDismiss()) {
          router.dismissTo(`/(root)/lounges/${loungeId}` as never)
        } else {
          router.replace(`/lounges/${loungeId}` as never)
        }
        return
      }

      await createPrivateSchedule({
        name: group.gameName.trim() ||  `${userName}'s Game`,
        hostId: userId,
        hostName: userName,
        scheduledStartTime: group.scheduleDate.getTime(),
        // 0 (not undefined) means "no blind increase" — see the lounge branch above.
        blindTimer: group.blindIncreases ? group.blindInterval : 0,
        startingChips: group.startingChips,
        turnDuration: group.turnDuration,
        tableConfigs: [],
        maxRebuys: group.rebuy ? group.rebuyCount : 0
      })

      router.back()
    } catch (error) {
      console.error('Failed to schedule game:', error)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
      <WebWrapper>
        <View style={styles.content}>
          <ScreenHeader title={translate('createGameV2.title')} />

          {/* No need for switcher if:
            - lounge: solo games can't be configured from a lounge
            - practice: explicitly requested by URL param
          */}
          {!(isLounge || isSimple) && (
            <View style={styles.segmentWrapper} onLayout={handleContainerLayout}>
              {containerWidth > 0 ? (
                <SegmentControl
                  inactiveSegments={TABS}
                  activeSegments={TABS}
                  parentWidth={containerWidth}
                  initialSelectedIndex={0}
                  onChangeIndex={setActiveTab}
                  borderRadius={8}
                />
              ) : null}
            </View>
          )}

          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {!isLounge && activeTab === 0 ? (
              <SoloTab ref={soloTabRef} allowCustomization={!isSimple}/>
            ) : (
              <GroupTab
                ref={groupTabRef}
                isLounge={isLounge}
                repeatDays={repeatDays}
                onRepeatDaysChange={setRepeatDays}
              />
            )}
          </ScrollView>
        </View>

        <View style={styles.footer}>
          {isCreating ? (
            <ActivityIndicator color={DS.palette['Global/White']} style={styles.loader} />
          ) : (
            <Button
              text={
                isSchedulingGroupGame ? translate('createGameV2.scheduleGame') : translate('createGameV2.startGame')
              }
              size="large"
              type="primary"
              onPress={
                !isLounge && activeTab === 0
                  ? handleStartSoloGame
                  : isSchedulingGroupGame
                    ? handleScheduleGroupGame
                    : handleStartGroupGame
              }
              containerStyle={[styles.startButton]}
              textStyle={[styles.startButtonText]}
            />
          )}
        </View>
      </WebWrapper>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  content: {
    flex: 1,
    gap: 32
  },
  segmentWrapper: {
    marginHorizontal: 16
  },
  scroll: {
    flex: 1
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8
  },
  startButton: {
    backgroundColor: DS.palette['Brand/Maroon'],
    borderRadius: 48,
    paddingVertical: 14.5
  },
  startButtonText: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 16,
    fontWeight: '500'
  },
  loader: {
    paddingVertical: 14
  }
})
